// src/App.tsx
import { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import HomePage from './pages/HomePage';
import { ToastProvider } from './contexts/ToastContext';
import { EmulatorSignIn } from './components/dev/EmulatorSignIn';
import { PlayerStatsProvider } from './contexts/PlayerStatsContext';
import { TRADE_SKILLS, tradePath } from "./data/production";
import { ChatFloatingButton } from './components/chat/ChatFloatingButton';
import { lazyPage } from './utils/lazyPage';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { useGameConfigVersion } from './config/gameConfig';

// Every page but the landing page is its own file, fetched when first opened (55.65, utils/lazyPage.ts).
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const WorldsPage = lazyPage(() => import('./pages/WorldsPage'));
const CoursesPage = lazyPage(() => import('./pages/CoursesPage'));
const TrainingPage = lazyPage(() => import('./pages/TrainingPage'));
const PatrolPage = lazyPage(() => import('./pages/PatrolPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const CasinoPage = lazyPage(() => import('./pages/CasinoPage'));
const BankPage = lazyPage(() => import('./pages/BankPage'));
const FeedbackPage = lazyPage(() => import('./pages/FeedbackPage'));
const AdminPage = lazyPage(() => import('./pages/AdminPage'));
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'));
const VIPPage = lazyPage(() => import('./pages/VIPPage'));
const PlotPage = lazyPage(() => import('./pages/PlotPage'));
const ShopMarketPage = lazyPage(() => import('./pages/ShopMarketPage'));
const CraftsPage = lazyPage(() => import('./pages/CraftsPage'));
const TradePage = lazyPage(() => import('./pages/TradePage'));
const DepartmentPage = lazyPage(() => import('./pages/DepartmentPage'));
const ProductionRedirect = lazyPage<object>(async () => ({ default: (await import('./pages/TradePage')).ProductionRedirect }));

function App() {
    // An admin edit to the game's configuration re-renders the whole tree (55.97, ROADMAP 2.3), so every page shows
    // the new values without a reload. Rare — only when an edit lands — and free: the config listener was already open.
    useGameConfigVersion();
    return (
        <Router>
            <ScrollToTop />
            <AuthProvider>
                <PlayerStatsProvider>
                        <ToastProvider>
                            <EmulatorSignIn />
                            <Suspense fallback={<div className="page gpage" aria-busy="true" />}>
                            <Routes>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/admin" element={<AdminPage />} />

                                {/* Signed in but no world chosen yet: the one protected
                                    page that does not require one. */}
                                <Route
                                    path="/worlds"
                                    element={
                                        <ProtectedRoute requireWorld={false}>
                                            <WorldsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute>
                                            <DashboardPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/courses"
                                    element={
                                        <ProtectedRoute>
                                            <CoursesPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/training"
                                    element={
                                        <ProtectedRoute>
                                            <TrainingPage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/patrol"
                                    element={
                                        <ProtectedRoute>
                                            <PatrolPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route path="/profile" element={
                                    <ProtectedRoute>
                                        <ProfilePage />
                                    </ProtectedRoute>
                                } />

                                {/* Pood / Turg, one page in two parts since 55.60; each part
                                    keeps its address so every existing link still lands. */}
                                <Route path="/shop" element={
                                    <ProtectedRoute>
                                        <ShopMarketPage part="pood" />
                                    </ProtectedRoute>
                                } />

                                {/* Osakond (55.78): the department's structure, open once the academy is done. */}
                                <Route path="/department" element={
                                    <ProtectedRoute>
                                        <DepartmentPage />
                                    </ProtectedRoute>
                                } />

                                <Route path="/house" element={
                                    <ProtectedRoute>
                                        <PlotPage />
                                    </ProtectedRoute>
                                } />

                                <Route path="/market" element={
                                    <ProtectedRoute>
                                        <ShopMarketPage part="turg" />
                                    </ProtectedRoute>
                                } />

                                {/* The trades, a page each since 55.59; /production, the old shared
                                    page, sends the player on to one of them. */}
                                {TRADE_SKILLS.map((skill) => (
                                    <Route key={skill} path={tradePath(skill)} element={
                                        <ProtectedRoute>
                                            <TradePage skill={skill} />
                                        </ProtectedRoute>
                                    } />
                                ))}
                                <Route path="/production" element={
                                    <ProtectedRoute>
                                        <ProductionRedirect />
                                    </ProtectedRoute>
                                } />

                                <Route path="/crafts" element={
                                    <ProtectedRoute>
                                        <CraftsPage />
                                    </ProtectedRoute>
                                } />

                                <Route path="/casino" element={
                                    <ProtectedRoute>
                                        <CasinoPage />
                                    </ProtectedRoute>
                                } />

                                <Route
                                    path="/bank"
                                    element={
                                        <ProtectedRoute>
                                            <BankPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/feedback"
                                    element={
                                        <ProtectedRoute>
                                            <FeedbackPage />
                                        </ProtectedRoute>
                                    }
                                />

                                {/* Account settings belong to the login, not to a character, so
                                    they must be reachable before a world is chosen — the account
                                    panel on /worlds links straight here. */}
                                <Route
                                    path="/settings"
                                    element={
                                        <ProtectedRoute requireWorld={false}>
                                            <SettingsPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/vip"
                                    element={
                                        <ProtectedRoute>
                                            <VIPPage />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                            </Suspense>

                            {/* Chat floating button - nähtav kõikidel kaitstud lehtedel */}
                            <ChatFloatingButton />
                        </ToastProvider>
                </PlayerStatsProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;